<!--
  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.
-->

<template>
  <div class="signature-wizard flex">

    <!-- side bar, navigate through steps -->
    <Sidebar
      :sidebarSteps="sidebarSteps"
      :currentId="pages[currentIndex]?.id"
      @changePage="setPageById"
    />

    <!-- main content split into current step and email skeleton preview on the right -->
    <div class="main flex">

      <!-- wrapper for current step, including header/step/footer -->
      <div class="designer-step flex-col justify-between">
        <Transition mode="out-in">
          <h1
            v-if="pages[currentIndex]"
            :key="'header-' + currentIndex"
            class="header mt-32 pb-16 px-16"
          >
            {{ pages[currentIndex]?.header }}
          </h1>
        </Transition>

        <!-- actually rendered step, see ./components/steps -->
        <div class="page-wrapper pb-16 pt-8 px-16">
          <Transition mode="out-in">
            <component
              v-if="pages[currentIndex]"
              :is="pages[currentIndex].component"
              :index="currentIndex"
              @prev="currentIndex--"
              @next="currentIndex++"
              @abort="close()"
            />
          </Transition>
        </div>

        <div class="footer flex justify-end p-16">
          <button v-if="currentIndex === 0" @click="close" class="btn btn-default me-16">{{ i18n.cancel }}</button>
          <button v-else @click="previousStep()" class="btn btn-default me-16">{{ i18n.back }}</button>
          <button
            v-if="currentIndex === sidebarSteps.findLastIndex(step => !step.disabled)"
            @click="save" class="btn btn-primary"
          >
            {{ i18n.save }}
          </button>
          <button v-else @click="nextStep()" class="btn btn-primary">{{ i18n.next }}</button>
        </div>
      </div>

      <EmailPreview class="preview"/>

      <IconButton
        class="btn btn-link close-designer absolute top-0 end-0 p-16"
        icon="bi/x-lg.svg"
        iconClass="bi-18"
        :title="i18n.close"
        :aria-label="i18n.close"
        @click="close()"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useSignatureDesignerStore } from '@/io.ox/mail/settings/signatures/signature-designer/store/signature-designer-store'
import { useSignatureWizard } from '@/io.ox/mail/settings/signatures/signature-designer/composables/useSignatureWizard'

import Sidebar from '@/io.ox/mail/settings/signatures/signature-designer/components/sidebar.vue'
import EmailPreview from '@/io.ox/mail/settings/signatures/signature-designer/components/email-preview.vue'
import IconButton from '@/io.ox/vueComponents/icon-button.vue'

import gt from 'gettext'

defineProps(['close', 'save'])

const {
  currentIndex,
  pages,
  sidebarSteps,
  setPageById,
  nextStep,
  previousStep
} = useSignatureWizard()

const store = useSignatureDesignerStore()
const { contactData } = storeToRefs(store)

const onClose = () => {
  store.clearKeepalive()
  store.resetContactData()
}

const i18n = {
  cancel: gt('Cancel'),
  next: gt('Next'),
  back: gt('Back'),
  save: gt('Save'),
  close: gt('Close')
}

store.socialFields.forEach(field => {
  watchDebounced(
    () => contactData.value[field],
    async (val) => {
      const iconField = `${field}IconUrl`

      if (!val || contactData.value[iconField]) return
      await store.ensureIconForField(field)
    },
    { debounce: 400, immediate: true }
  )
})

store.textFields.forEach(field => {
  watchDebounced(
    () => contactData.value[field],
    async (val) => {
      const iconField = `${field}IconUrl`

      if (!val || contactData.value[iconField]) return
      await store.ensureIconForField(field)
    },
    { debounce: 400, immediate: true }
  )
})

onMounted(() => store.resetContactData())
defineExpose({ onClose })
</script>

<style lang="scss" scoped>
:deep(.v-enter-active, .v-leave-active) {
  transition: opacity .3s ease;
}

:deep(.v-enter-from, .v-leave-to) {
  opacity: 0;
}

:deep(form) { column-gap: 6px; }
:deep(.form-item:not(:first-child)) { margin-top: 24px; }

:deep(.signature-wizard .signature-preview) {
  width: 100%;
  overflow: hidden;
}

.signature-wizard {
  height: 100%;
}

.main {
  width: 100%;

  .designer-step {
    width: 40%;

    .header {
      font-size: 1.5rem;
    }

    .page-wrapper {
      overflow: auto;
      height: 100%;
      :deep(h1) {
        margin-top: 12px;
      }
    }

    .footer {
      text-align: end;
      border-top: 1px solid var(--border);
    }

    :deep(.label) {
      display: inline-block;
      max-width: 100%;
      margin-bottom: 5px;
      line-height: 20px;
      font-weight: 700;
      color: var(--text);
      padding: 0;
      &:not(.text-medium) { font-size: 13px; }
    }
  }

  .close-designer {
    background-color: var(--accent-a10);
    width: 32px;
    height: 32px;
    top: 16px;
    right: 16px;
    border-radius: 16px;
  }
}

@media (max-width: 1024px) {
  .main {
    .designer-step { width: 50%; }
    .preview { width: 50%; }
  }
}
</style>

<style lang="scss">
.io-ox-signature-designer.modal.flex {
  .modal-dialog {
    max-width: min(1200px, 100%);
    width: max(850px, 90%);
    height: 100%;
  }

  .modal-content {
    height: calc(100vh - 64px);
  }

  @media (max-width: $smartphone-breakpoint) and (orientation: portrait), (max-height: $smartphone-breakpoint) and (orientation: landscape) {
    .modal-content {
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
      box-shadow: 0 0 70px 10px rgba(0, 0, 0, 0.5);
    }

    &.in {
      top: var(--offset-top);
    }
  }
}
</style>
